import { z } from 'zod'

import {
  type BulkyGroup,
  type FreeSpace,
  placeBulkyUnits,
  unitsOfShapeIn,
  unitsThatFit,
} from './pallet-placement'

/**
 * What a Stopal pallet physically is, and the math that turns a list of
 * measured belongings into a pallet count.
 *
 * These numbers live in exactly one place. Nothing can compute against a
 * sentence, so putting them only as prose inside a marketing FAQ answer
 * would force anything needing the interior height to retype it, which is
 * the second hand-typed copy `20-architecture.md` forbids. This module is
 * the one declaration; the FAQ renders its sentence from these values rather
 * than restating them.
 *
 * `@stopal/shared` is the home because every consumer can reach it: the
 * estimator math runs in the browser (so pruning an item recomputes with no
 * network call) and on the server (grounding the AI and validating its output).
 */

const CUBIC_INCHES_PER_CUBIC_FOOT = 1728

/**
 * The usable interior — what a customer can actually fill. Inches.
 *
 * Deliberately smaller than the exterior footprint: the deck, walls and lid
 * take the difference.
 */
export const PALLET_INTERIOR = {
  lengthInches: 44.5,
  widthInches: 36.5,
  heightInches: 40.7,
} as const

/** The exterior footprint — what a pallet occupies on a floor. Inches. */
export const PALLET_EXTERIOR = {
  lengthInches: 48,
  widthInches: 40,
} as const

/** Load capacity of one pallet, in pounds. */
export const PALLET_CAPACITY_POUNDS = 2000

/** Raw interior volume, cubic inches. */
export const PALLET_INTERIOR_CUBIC_INCHES =
  PALLET_INTERIOR.lengthInches *
  PALLET_INTERIOR.widthInches *
  PALLET_INTERIOR.heightInches

/** Raw interior volume, cubic feet — what the FAQ quotes. */
export const PALLET_INTERIOR_CUBIC_FEET =
  PALLET_INTERIOR_CUBIC_INCHES / CUBIC_INCHES_PER_CUBIC_FOOT

/**
 * The ceiling the ESTIMATE clamps a quantity to. A model proposal is not a
 * persisted value, so an absurd quantity is clamped rather than rejected — one
 * bad line must not fail an otherwise good estimate.
 *
 * Deliberately not named as the project-wide item-quantity bound: the inventory
 * write boundary declares its own identical ceiling (`itemQuantity` in
 * `user-pallets.schemas.ts`), and retiring that copy is a change to shipped
 * inventory code rather than to the estimator.
 */
export const MAX_ESTIMATED_ITEM_QUANTITY = 999

/**
 * The most pallets one order may carry.
 *
 * A domain ceiling rather than a widget's cap, which is why it lives here: the
 * stepper enforces it when a human picks the number, the estimator clamps to it
 * when the maths produces one, and the checkout URL validator clamps to it when
 * the number arrives from a link a stranger wrote. Many enforcers, one number —
 * a cap the stepper honours while the URL does not is not a cap.
 *
 * **Deliberately not counted.** This sentence carried a tally for a while; it
 * was wrong the first time the list changed, corrected, and immediately
 * ambiguous again (are the checkout and quote schemas one enforcer or two?).
 * The load-bearing claim is that every enforcer reads THIS constant, which a
 * count neither states nor protects.
 *
 * The server-side ones are what make the browser ones bounds rather than
 * manners: the three above all run in a BROWSER, so for as long as they were
 * the whole list a hand-built request carried any count it liked. The checkout
 * input schemas (`checkout.schemas.ts`) enforce it as a per-ORDER total — this
 * constant says "one order", and a per-line ceiling under an array bound is a
 * different, larger number — and the quote schema (`pricing.schemas.ts`)
 * carries the same total as a scalar so a quote cannot price a basket the
 * charge refuses.
 *
 * It is also what keeps a route validator from importing an order-flow
 * component: `handoff-search.ts` runs during route matching, so reading this
 * from the pill would drag that component (and its icon packages) into the
 * eagerly-loaded entry chunk of every page, including the landing page.
 */
export const MAX_PALLET_QUANTITY = 99

/**
 * One thing the customer wants stored, with the measurements the count is
 * computed from.
 *
 * This is the shape the AI features return, so it is also what
 * `z.toJSONSchema` hands the model. Two constraints follow from that and must
 * be kept:
 *
 * - **This schema must reach a provider only through `generateStructuredJson`**,
 *   never by being handed to one directly. `stripUnsupportedNumericBounds`
 *   removes the range keywords on the way, and they are all over this shape:
 *   `.int()` emits the JS safe-integer range on `quantity`, and
 *   `.nonnegative()` emits `minimum: 0` on all four measurements.
 * - **Never add a bound the strip does not remove** — a string `.max()`, a
 *   `.regex()`, an array `.min()`. Those emit `maxLength`/`pattern`/`minItems`,
 *   which providers reject and nothing strips, so the model call simply fails.
 *   Text ceilings belong at the app's own boundary, applied after the model
 *   answers (`estimator.schemas.ts` is the worked example).
 * - **The numeric bounds still do real work in VALIDATION**, which is why they
 *   stay: a negative dimension would silently shrink a total rather than fail.
 *   They are enforced when the response is parsed, not by the model.
 *
 * Number bounds are not safe to send. That belief would ship a public
 * endpoint whose every model call a provider would refuse — invisibly, because
 * nothing in the suite sends a real schema to a real provider.
 * `lib/ai/wire-schema.test.ts` is the check.
 *
 * Zod rejects `NaN` and `Infinity` for `z.number()`, so a parsed item is always
 * finite. `assessFit` re-checks anyway, because the browser builds items from
 * typed input without going through this schema.
 *
 * `category` is free text, matching the shipped inventory extraction —
 * `INVENTORY_CATEGORIES` is the suggested vocabulary, not an enforced one.
 */
export const measuredItemSchema = z.strictObject({
  name: z.string(),
  category: z.string().nullable(),
  quantity: z.number().int(),
  lengthInches: z.number().nonnegative(),
  widthInches: z.number().nonnegative(),
  heightInches: z.number().nonnegative(),
  weightPounds: z.number().nonnegative(),
  /**
   * How sure the model is of the measurements. Surfaced per item so a customer
   * knows which lines are worth correcting — an estimate is only as good as the
   * dimensions behind it.
   */
  confidence: z.enum(['low', 'medium', 'high']),
  /**
   * Whether this gets packed INTO a moving box rather than riding as it is.
   *
   * The model's call, not the geometry's, and that is the whole reason it is
   * asked for: a mirror fits a box's dimensions and must not go in one, a duvet
   * fits nothing and goes in one anyway. Nothing derivable from three numbers
   * separates those.
   *
   * It decides which of two very different costs a unit is charged
   * (its own volume boxed, real placement against the interior if not), so
   * it moves the count.
   *
   * **A rigid carton misread as `boxable` under-quotes, and the prompt is the
   * whole mitigation — there is no guard behind it.** A boxed unit is charged
   * raw volume with no geometric cap, so ten 22 x 18 x 16 cartons answer ONE
   * pallet with this flag set and two without it, where eight of the
   * catalogue's own Swift Box tile a pallet and ten need two. The prompt names
   * cartons, crates, suitcases, bins and totes explicitly and a test pins that
   * clause; nothing else does.
   *
   * That is also why the kit catalogue does not evidence the ruling this flag
   * carries. The kits are a uniform 12 boxes per pallet, and that ratio is
   * real, but 12 boxes at the dimensions in their `description` strings come to
   * about 139% of the interior — the surplus is spare cardboard. Nor can those
   * dimensions be leaned on: `product` carries no dimension columns.
   *
   * (Both facts lived on a helper this branch deleted, and a reviewer caught
   * that they survived nowhere else.)
   *
   * **Required, with no `.default()`, and that is a decision about the WIRE.**
   * A default is emitted into the JSON schema as a `default` keyword, and this
   * shape is handed to a provider — where an unrecognised keyword is refused
   * for the whole call, invisibly, because nothing in the suite sends a real
   * schema to a real provider. That is the exact way `minimum: 0` shipped a
   * public endpoint every model call would have 400'd. A bare boolean is
   * accepted everywhere.
   *
   * Tolerance for a value that is genuinely absent belongs at the boundaries
   * that can SEE one, and there are two:
   *
   * - `estimator-snapshot.ts`, reading a list saved by a build that predates
   *   this field — a genuine transition, and it will stop mattering.
   * - `estimatorItemInputSchema`, receiving a confirm from the browser. That
   *   one is **permanent**: the client deliberately does not send this field
   *   (`toAdviceItems`), so the default there is what makes every real request
   *   parse, not a bridge across one deploy. Read its comment before touching
   *   it.
   *
   * Everything else constructs the item in TypeScript, where a missing field
   * does not compile.
   */
  boxable: z.boolean(),
})

export type MeasuredItem = z.infer<typeof measuredItemSchema>

/**
 * Why an item is not counted onto a pallet.
 *
 * `unmeasured` is not a statement about the object — it means we could not read
 * a usable size for it. It still belongs here rather than in the count, because
 * the alternatives are both worse: summing an unreadable measurement poisons
 * the whole estimate, and dropping the line silently loses something the
 * customer told us they are shipping.
 */
export const FIT_FAILURES = [
  'too_long',
  'too_tall',
  'too_wide',
  'too_heavy',
  'unmeasured',
] as const

export type FitFailure = (typeof FIT_FAILURES)[number]

export type FitVerdict =
  | { fits: true }
  | { fits: false; reasons: [FitFailure, ...FitFailure[]] }

/**
 * The interior's three axes, largest first, each paired with the refusal it
 * produces — derived from the spec rather than written out, so changing a
 * dimension cannot leave the reason naming the wrong axis.
 */
interface InteriorAxis {
  inches: number
  failure: FitFailure
}

const INTERIOR_AXES: readonly InteriorAxis[] = (
  [
    { inches: PALLET_INTERIOR.lengthInches, failure: 'too_long' },
    { inches: PALLET_INTERIOR.heightInches, failure: 'too_tall' },
    { inches: PALLET_INTERIOR.widthInches, failure: 'too_wide' },
  ] satisfies InteriorAxis[]
).sort((a, b) => b.inches - a.inches)

/**
 * Can this item go on a pallet at all?
 *
 * A refusal here is not "buy another pallet" — an item longer than the interior
 * cannot be palletized however many pallets are bought, so it has to be named
 * to the customer rather than folded into a number.
 *
 * The test is **orientation-free**: both the item's dimensions and the
 * interior's are sorted largest-first and compared pairwise, because a
 * 40 x 44 x 36 item fits an interior it would fail on a naive
 * height-against-height check. Sorting is also what makes the reason exact
 * rather than approximate — the interior's largest axis IS its length, its
 * second its height, its third its width, so the failing slot names a real
 * dimension.
 *
 * Every applicable reason is reported: a piano is both too long and too heavy,
 * and naming one of the two invites a customer to solve the wrong problem.
 */
export function assessFit(item: MeasuredItem): FitVerdict {
  // A non-finite measurement fails every comparison below silently — `NaN > 40`
  // is false — so the item would read as fitting and then turn the whole
  // estimate into NaN, which serializes to `null`. Refuse it up front, and
  // refuse it alone: nothing about an item we could not measure is knowable
  // enough to also call it too tall.
  //
  // A zero DIMENSION is the same failure wearing a plausible number: nothing
  // occupies no space, so a zero is a size we could not read. Counted as a fit
  // it contributes nothing to either total and disappears from a mixed list
  // without ever being named. A zero WEIGHT is not the same — plenty of things
  // weigh nothing worth recording — so it is left alone.
  const dimensionValues = [
    item.lengthInches,
    item.widthInches,
    item.heightInches,
  ]
  const unreadable =
    !Number.isFinite(item.weightPounds) ||
    dimensionValues.some((value) => !Number.isFinite(value) || value <= 0)
  if (unreadable) {
    return { fits: false, reasons: ['unmeasured'] }
  }

  const dimensions = [...dimensionValues].sort((a, b) => b - a)

  const reasons: FitFailure[] = []
  for (const [index, axis] of INTERIOR_AXES.entries()) {
    const dimension = dimensions[index]
    if (dimension !== undefined && dimension > axis.inches) {
      reasons.push(axis.failure)
    }
  }
  // Weight is per item, so quantity is irrelevant here — a single unit over the
  // capacity can never ride, and units under it are a counting problem the
  // estimate solves by adding pallets.
  if (item.weightPounds > PALLET_CAPACITY_POUNDS) reasons.push('too_heavy')

  const [first, ...rest] = reasons
  return first === undefined
    ? { fits: true }
    : { fits: false, reasons: [first, ...rest] }
}

export interface UnfittableItem {
  item: MeasuredItem
  reasons: [FitFailure, ...FitFailure[]]
}

export interface PalletEstimate {
  /** How many pallets this list needs — the answer the customer asked for. */
  pallets: number
  /** What volume alone would need. Exposed so a surface can say which limit bound. */
  palletsByVolume: number
  /** What weight alone would need. */
  palletsByWeight: number
  totalVolumeCubicFeet: number
  totalWeightPounds: number
  /** The items the count covers. */
  palletable: MeasuredItem[]
  /**
   * Items not counted onto a pallet, with why. Never silently dropped.
   *
   * Not all of these are physical refusals: `unmeasured` is a missing-input
   * state whose remedy is a measurement, not a smaller sofa — so a surface
   * splits the list rather than heading all of it as impossible.
   */
  unfittable: UnfittableItem[]
}

/** Quantity reaches this from a model, so treat it as a proposal, not a fact. */
function unitsOf(item: MeasuredItem): number {
  if (!Number.isFinite(item.quantity)) return 1
  return Math.min(
    Math.max(Math.trunc(item.quantity), 1),
    MAX_ESTIMATED_ITEM_QUANTITY,
  )
}

/** So many units of one size — a customer line, or all lines sharing a size. */
interface SizedUnits {
  size: number
  units: number
}

/**
 * How many live entries of already-open pallets the first-fit scan retains.
 *
 * An unbounded scan is quadratic in the number of distinct sizes, and dimensions
 * are arbitrary floats, so an attacker makes every unit a distinct size for the
 * price of a larger JSON body. Bounding it is safe in the one direction that
 * matters: a top-up the scan fails to find opens another pallet rather than
 * skipping one, so the arrangement stays achievable and the count can only rise.
 *
 * **A green suite is not proof this value is unnecessary**, and no stable test
 * can be written for it. Measured at the time of writing: removing the bound
 * costs 83ms against 56ms on 20,000 distinct sizes and nothing observable on
 * accuracy, so any timing assertion tight enough to catch it would be flaky on
 * slower hardware, and any accuracy assertion would be vacuous. What the test
 * suite does guard is the catastrophic case — a return to per-unit packing,
 * which took 2.7 SECONDS on a payload this shape.
 */
const FIRST_FIT_LOOKBACK = 64

/**
 * Actually pack the units against a per-pallet capacity, rather than dividing a
 * total by it. Used for BOTH constraints: pounds against the weight capacity,
 * cubic inches against the interior volume.
 *
 * **The point is the direction of the error.** The arrangement it finds is one
 * that genuinely works — no pallet is over capacity — so the count can never
 * fall below what the load really needs. Under-counting is the failure that
 * quotes a customer a load which cannot physically ship. It is not the
 * theoretical optimum, which is NP-hard and would be false precision on top of
 * dimensions a model guessed from a photo; it is first-fit-decreasing, which
 * lands on the optimum for essentially every real load.
 *
 * **Dividing a total silently assumes belongings are pourable.** Three 1,200 lb
 * units total 3,600 lbs and divide to two pallets, but only one fits under the
 * 2,000 lb capacity, so the truth is three. Weight has no slack at all.
 *
 * The same hole on volume — ten washing machines each taking two thirds of a
 * pallet divide to seven, when only one goes on a pallet and the answer is
 * ten. **That example does not belong to the VOLUME call**, and the
 * axis is the whole of the distinction: a washing machine is bulky, so its
 * volume is placed by `placeBulkyUnits` instead. Its WEIGHT still arrives here
 * — every palletable item does, `boxable` or not — so this function is called
 * twice with two different populations, and only one of them is boxed goods.
 *
 * Packing rather than dividing earns its keep on both. On volume, a boxed unit
 * taking two thirds of a pallet is still indivisible, so ten of them are ten
 * pallets and not seven. On weight, nothing is divisible at all.
 *
 * **The answer depends only on the multiset of unit sizes.** Sizes are summed
 * across lines before anything is packed, so it cannot matter whether the
 * customer's five 700 lb boxes arrived as one line of five or five lines of
 * one. Every bound that reasoned per LINE got this wrong, and photo extraction
 * is TOLD to merge duplicates onto one row, so a split list is what arrives
 * when it does not obey, when a customer types their own, or when two objects
 * are alike enough to read as one.
 *
 * **Neither the pallets nor the lookback are walked one at a time**, and both
 * bounds are load-bearing on a public endpoint. Pallets opened for one size all
 * have the same room left, so they are held as a single entry — otherwise one
 * line of 999 boxes opens 999 entries that every later size then re-scans, and
 * a 166 KiB body buys seconds of CPU. The first-fit scan then looks back over
 * only the most recent entries. Missing a top-up can only OPEN a pallet, never
 * close one, so a bounded scan keeps the arrangement feasible and can only push
 * the count up — the same direction-of-error argument the whole function rests
 * on. Cost is O(distinct sizes), with no factor a caller has to promise to
 * bound: this module clamps quantity itself, so a caller capping its line count
 * could not bound the pallet count from outside even if it tried.
 */
function packUnits(groups: SizedUnits[], capacity: number): number {
  // Collapse equal sizes. This is LOAD-BEARING for the module's headline
  // promise, not merely a cost measure — remove it and a list of 30 sizes at 10
  // units each answers 150 pallets merged and 167 split. Sorting alone looks
  // sufficient on short lists and stops being so past FIRST_FIT_LOOKBACK: each
  // line then pushes its own entries, `open` outgrows the scan window, and a
  // later line of the same size cannot see the room an earlier one left.
  // A split list arrives whenever the model does not obey its merge
  // instruction, or the customer built the list by hand. It buys the cost too — the loop below is O(distinct sizes), so a line
  // of 999 boxes costs one iteration rather than 999.
  const unitsBySize = new Map<number, number>()
  for (const group of groups) {
    if (group.size <= 0 || group.units <= 0) continue
    unitsBySize.set(
      group.size,
      (unitsBySize.get(group.size) ?? 0) + group.units,
    )
  }

  // Pallets that share a remaining capacity, held once with a count. Kept
  // compact: see the trim below.
  //
  // Always starts empty. Seeding it with pallets the bulky placement had
  // already opened, carrying their leftover VOLUME, would pour boxed goods
  // into gaps no box could reach. Free room is a shape, not
  // a number, so it is spent by `rideInFreeSpaces` before anything gets here,
  // and what arrives is only what needs a pallet of its own.
  let open: { free: number; count: number }[] = []
  let pallets = 0

  const largestFirst = [...unitsBySize.entries()].sort(
    ([left], [right]) => right - left,
  )
  for (const [size, units] of largestFirst) {
    let unplaced = units

    // Top up pallets already open, most recent first — sizes are walked
    // largest-first, so the newest entries are the ones a size just ABOVE this
    // one left room in, and they are where a smaller unit is likeliest to fit.
    const opened: { free: number; count: number }[] = []
    for (let i = open.length - 1; i >= 0 && unplaced > 0; i--) {
      const slot = open[i]
      if (!slot || slot.count <= 0) continue
      const perSlot = unitsThatFit(slot.free, size)
      if (perSlot <= 0) continue

      const filled = Math.min(slot.count, Math.floor(unplaced / perSlot))
      const remainder = unplaced - filled * perSlot
      const partial = filled < slot.count && remainder > 0 ? 1 : 0

      slot.count -= filled + partial
      if (filled > 0) {
        opened.push({ free: slot.free - perSlot * size, count: filled })
      }
      if (partial > 0) {
        opened.push({ free: slot.free - remainder * size, count: 1 })
      }
      unplaced -= filled * perSlot + (partial > 0 ? remainder : 0)
    }

    // At least one, always — and this is a floor with **no reachable case
    // through either call**, which is worth stating rather than leaving to be
    // rediscovered. A bulky unit's VOLUME is placed before this point and never
    // arrives here (its weight still does, like everything else's); were it
    // charged `volume / LOOSE_STACK_EFFICIENCY` here instead, that charge could
    // exceed the interior and `assessFit` and `estimatePallets` would
    // contradict each other about the same wardrobe. Both populations that do arrive
    // are bounded by `assessFit` — a boxed unit's volume is at most the
    // interior, any unit's weight at most the capacity — so `unitsThatFit`
    // cannot return zero.
    //
    // **Kept, and a green suite is not evidence it is unnecessary.** Verified
    // by mutation: removing it kills nothing in the shared suite.
    //
    // Without it, a size LARGER THAN THE CAPACITY makes `unitsThatFit` return
    // zero, `Math.floor(unplaced / 0)` infinite, and the function returns
    // `Infinity` pallets. Two earlier drafts of this note got the failure wrong
    // in different ways — one called it a hang (`packUnits` has no unbounded
    // loop), the next blamed a zero size, which the `group.size <= 0` filter
    // forty lines up has already dropped. A quote of `Infinity` is still a
    // worse failure than the line costs, and "no caller can do that today" is a
    // statement about today's callers, not about the function.
    const perPallet = Math.max(unitsThatFit(capacity, size), 1)
    if (unplaced > 0) {
      const full = Math.floor(unplaced / perPallet)
      const remainder = unplaced - full * perPallet
      if (full > 0) {
        // Clamped: an oversized single unit fills its pallet rather than
        // leaving it with negative room for the next size down.
        opened.push({
          free: Math.max(capacity - perPallet * size, 0),
          count: full,
        })
        pallets += full
      }
      if (remainder > 0) {
        opened.push({
          free: Math.max(capacity - remainder * size, 0),
          count: 1,
        })
        pallets += 1
      }
    }
    // Appended after the scan, never during it — a pallet this size just
    // touched must not be rescanned for this same size.
    for (const slot of opened) open.push(slot)

    // Trim to the most recent LIVE entries. An entry dies as soon as its
    // pallets are topped up, and dead ones left in place still occupy the
    // window the scan is bounded to — measured at 62-64% of the array, so a
    // 64-entry window was really looking at ~20-35 pallets and a 200-line list
    // was quoted three pallets high. Dropping an open pallet from view can only
    // cause another to be OPENED, never closed, so this keeps the arrangement
    // feasible and can only push the count up; recovering the lost tightness is
    // what it is for. It also bounds `open` outright instead of letting it grow
    // with the number of distinct sizes.
    if (open.length > FIRST_FIT_LOOKBACK) {
      const live = open.filter((slot) => slot.count > 0)
      open =
        live.length > FIRST_FIT_LOOKBACK
          ? live.slice(live.length - FIRST_FIT_LOOKBACK)
          : live
    }
  }
  return pallets
}

/**
 * Let boxed goods ride in the empty boxes the furniture left, and hand back
 * whatever still needs a pallet of its own.
 *
 * **A unit rides only where it FITS** — leftover *volume* alone is not room
 * for a carton whose dimensions do not fit. A 44 x 18 x 40 wardrobe leaves an
 * 18.5"-wide strip worth 34,427 cubic inches; a 40 x 30 x 28 carton — 33,600,
 * but 28" across its smallest face — fits that volume and not that strip. Two
 * sweeps found this in 15% and 17% of mixed loads. The exhaustive
 * placement oracle covers bulky-only loads, so it cannot see this case.
 *
 * **Testing a boxed unit by its own measured dimensions is deliberately
 * strict.** `boxable` means the goods get repacked into moving boxes, so the
 * measurement describes the object loose rather than the carton it ends up in —
 * a duvet measures 60 x 40 x 20 and travels in an 18" box. Judging it by the
 * loose measurement therefore refuses some units that would really fit. That is
 * the safe direction, and it is as far as this can go honestly: knowing what a
 * moving box measures needs dimensions on `product`, which carries none (they
 * are prose inside a `description`).
 *
 * Each empty box serves **one** shape. Once a space has taken units it is spent,
 * even if room is left — subdividing it would mean tracking what the remainder
 * looks like, and an unsubdivided leftover only ever costs a quote air.
 *
 * Largest shape first: a big object has the fewest places it can go, so letting
 * a small one take the only space that fits it is how a greedy pass wastes one.
 */
function rideInFreeSpaces(
  spaces: readonly FreeSpace[],
  boxed: readonly BulkyGroup[],
): SizedUnits[] {
  // Collapse identical shapes first, exactly as the placement does, and for the
  // same reason: a space serves one shape, so leaving five identical cartons as
  // five separate lines would consume five spaces where one takes all of them.
  // A split list is reachable whenever the model does not obey its merge
  // instruction, or the customer built the list by hand — and this broke
  // grouping invariance on 308 of 4,000 mixed loads before the collapse was
  // here.
  const byShape = new Map<
    string,
    { dims: readonly [number, number, number]; units: number; volume: number }
  >()
  for (const group of boxed) {
    const key = [...group.dims].sort((a, b) => b - a).join('x')
    const existing = byShape.get(key)
    if (existing) {
      existing.units += group.units
      continue
    }
    byShape.set(key, {
      dims: group.dims,
      units: group.units,
      volume: group.dims[0] * group.dims[1] * group.dims[2],
    })
  }
  const remaining = [...byShape.values()]
  const largestFirst = [...remaining].sort((a, b) => b.volume - a.volume)

  for (const space of spaces) {
    let instances = space.count
    for (const group of largestFirst) {
      if (instances <= 0) break
      if (group.units <= 0) continue
      const perSpace = unitsOfShapeIn(space, group.dims)
      if (perSpace <= 0) continue
      const spacesUsed = Math.min(instances, Math.ceil(group.units / perSpace))
      group.units = Math.max(group.units - spacesUsed * perSpace, 0)
      instances -= spacesUsed
    }
  }

  return remaining
    .filter((group) => group.units > 0)
    .map((group) => ({ size: group.volume, units: group.units }))
}

/**
 * How many pallets a confirmed list needs.
 *
 * **Volume and weight are independent constraints**, so the answer is whichever
 * binds harder rather than whichever a caller happened to check. Volume is what
 * binds on almost everything: weight only overtakes it above roughly 80 lb per
 * cubic foot of goods, which household furniture and even boxed books never
 * reach. It takes gym plates, boxed tiles or a safe — a hundred 18x18x2 plates
 * at 45 lb each want two pallets by volume and three by weight.
 *
 * **Neither constraint is a division.** Loading pallets is bin packing on the
 * weight axis and on the boxed half of the volume axis, and real placement on
 * the bulky half. Dividing a total silently assumes belongings are pourable:
 * three 1,200 lb units total 3,600 and divide to two pallets, when only one
 * fits under the 2,000 lb capacity and the truth is three.
 *
 * **Two 1,200 lb units is NOT that example** — they divide to 1.2 and ceil to
 * two, which is the right answer. "Two pallets for one bulky item `assessFit`
 * calls a fit" is not the over-counting direction either: a bulky item's raw
 * volume is at most the interior. Only the under-counting direction holds, and
 * it needs three units to show it.
 *
 * See `packUnits` for why the arrangement it finds is deliberately achievable
 * rather than optimal.
 *
 * **The pallet is never discounted, and neither is an item.** Goods
 * packed into boxes cost their own volume, so a boxed load reaches the whole
 * interior. Goods that ride as they are are **placed** — laid against the
 * 44.5 x 36.5 x 40.7 interior by `placeBulkyUnits` — and the room those pallets
 * have left is then what the boxes fill. Neither half is a factor: a load's
 * waste is a fact about the shapes in it, which is exactly what a constant
 * could not say.
 *
 * **The order matters and is not arbitrary.** Bulky first, boxes into the gaps,
 * because it is the only order that is achievable: a wardrobe cannot be poured
 * into whatever a box left, and boxes genuinely do go around furniture. Packing
 * the boxes first and the furniture after would quote a load nobody can load.
 *
 * **`placeBulkyUnits` promises never to under-count what IT reports. This
 * function does not inherit that promise, and the difference is the ruling
 * rather than a defect.** Boxed goods that find no gap are poured onto pallets
 * of their own at the full interior, which is what Marcus ruled and what makes
 * a boxed load reach the 38 cu ft the FAQ quotes — and it is an idealisation:
 * three 23 x 24 x 25 cartons pour into one pallet where, as rigid boxes, no two
 * of them share. Measured against an exhaustive search treating boxed goods as
 * rigid, this function answers below the truth on 2 of 400 small mixed loads,
 * against 26 of 400 for the constant it replaces. Closing the rest means
 * knowing what a moving box measures, which needs dimensions on `product`.
 *
 * Items that cannot be palletized are **partitioned out rather than counted**.
 * Folding a piano's volume into the total would quietly sell the customer a
 * pallet that cannot carry it; they are returned in `unfittable` so the surface
 * has to say so.
 */
export function estimatePallets(items: MeasuredItem[]): PalletEstimate {
  const palletable: MeasuredItem[] = []
  const unfittable: UnfittableItem[] = []
  for (const item of items) {
    const verdict = assessFit(item)
    if (verdict.fits) palletable.push(item)
    else unfittable.push({ item, reasons: verdict.reasons })
  }

  let rawCubicInches = 0
  let totalWeightPounds = 0
  const boxedGroups: BulkyGroup[] = []
  const bulkyGroups: BulkyGroup[] = []
  const weightGroups: SizedUnits[] = []
  for (const item of palletable) {
    const units = unitsOf(item)

    // The GOODS' own volume, which is what the rundown shows a customer, and
    // never what the pack is computed from — a bulky item denies room its own
    // dimensions do not account for, and printing that back as "your
    // belongings" would quote them air they do not own.
    rawCubicInches +=
      item.lengthInches * item.widthInches * item.heightInches * units
    totalWeightPounds += item.weightPounds * units

    if (item.boxable) {
      boxedGroups.push({
        dims: [item.lengthInches, item.widthInches, item.heightInches],
        units,
      })
    } else {
      bulkyGroups.push({
        dims: [item.lengthInches, item.widthInches, item.heightInches],
        units,
      })
    }
    weightGroups.push({ size: item.weightPounds, units })
  }

  // Bulky items are laid out first; boxed goods then ride in the gaps the
  // furniture left, and only what does not fit a gap opens a pallet of its own.
  // With no bulky items there are no gaps, and the second step is the plain
  // bin-pack against the full interior it has always been — which is what makes
  // a boxed load still reach the 38 cu ft the FAQ quotes.
  const placement = placeBulkyUnits(bulkyGroups, PALLET_INTERIOR)
  const spilled = rideInFreeSpaces(placement.freeSpaces, boxedGroups)
  const palletsByVolume =
    placement.pallets + packUnits(spilled, PALLET_INTERIOR_CUBIC_INCHES)
  const palletsByWeight = packUnits(weightGroups, PALLET_CAPACITY_POUNDS)

  return {
    // Anything the customer is shipping needs somewhere to ride, so a list with
    // palletable items never answers zero.
    //
    // The `1` is belt-and-braces and hard to cover with a test: a palletable
    // item has positive dimensions, so `palletsByVolume` is already >= 1 here
    // and a green suite is NOT proof this floor is unnecessary. It is not
    // ornamental either — a wholly weightless list packs to zero pallets by
    // weight, and the cheap arrangement above returns zero for one too. It
    // states the invariant where the answer is returned, so a later change to
    // either count cannot quietly reintroduce a zero.
    pallets:
      palletable.length > 0 ? Math.max(1, palletsByVolume, palletsByWeight) : 0,
    palletsByVolume,
    palletsByWeight,
    // The goods' real volume, not the packed figure the count is derived from —
    // this is the number a human checks the estimate against.
    totalVolumeCubicFeet: rawCubicInches / CUBIC_INCHES_PER_CUBIC_FOOT,
    totalWeightPounds,
    palletable,
    unfittable,
  }
}

/**
 * Copy helpers, so a surface quoting the spec renders the same numbers the math
 * runs on. `toLocaleString` is deliberately avoided — it depends on the
 * runtime's ICU data, and these strings are asserted byte-for-byte.
 */
export function formatInches(inches: number): string {
  return `${inches}"`
}

export function formatPounds(pounds: number): string {
  // Group the whole part only. Run over the whole string, the separator regex
  // also groups digits after the decimal point ("1234.5678" -> "1,234.5,678").
  const [whole, fraction] = String(pounds).split('.')
  const grouped = (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${fraction === undefined ? grouped : `${grouped}.${fraction}`} lbs`
}

/**
 * The rounded interior volume in cubic feet, as a bare numeral — the caller
 * supplies the unit, because it is interpolated mid-sentence. Unlike
 * `formatInches` and `formatPounds`, this carries no unit of its own.
 */
export function formatInteriorCubicFeet(): string {
  return String(Math.round(PALLET_INTERIOR_CUBIC_FEET))
}
